<template>
  <BaseModule
    v-bind="$props"
    class="container module-form"
    data-module="form-lgpd"
  >
    <div class="content">
      <header
        v-if="title || subtitle || text"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
      </header>

      <div class="formulario">
        <form
          class="form"
          novalidate
          @submit.prevent="send"
        >
          <div class="field-2">
            <div
              class="form-field"
              :class="{ error: !isValidContactName }"
            >
              <FloatingLabel :for="`name-${moduleId}`">{{
                $t('placeHolderName')
              }}</FloatingLabel>
              <input
                :id="`name-${moduleId}`"
                v-model.trim="contactName"
                name="name"
                type="text"
                @blur="startValidate('name')"
              />
            </div>
            <div
              class="form-field"
              :class="{ error: !isValidContactCpf }"
            >
              <FloatingLabel :for="`cpf-${moduleId}`">CPF*</FloatingLabel>
              <input
                :id="`cpf-${moduleId}`"
                v-model="maskedContactCpf"
                v-imask="contactCpfMask"
                name="cpf"
                type="text"
                maxlength="14"
                @accept="updateContactCpf"
                @blur="startValidate(`cpf`)"
              />
            </div>
          </div>

          <div class="field-2">
            <div
              class="form-field"
              :class="{ error: !isValidContactEmail }"
            >
              <FloatingLabel :for="`email-${moduleId}`">E-mail*</FloatingLabel>
              <input
                :id="`email-${moduleId}`"
                v-model.trim="contactEmail"
                name="email"
                type="email"
                @blur="startValidate(`email`)"
              />
            </div>
            <div
              class="form-field"
              :class="{ error: !isValidContactPhone }"
            >
              <FloatingLabel :for="`phone-${moduleId}`">{{
                $t('placeHolderPhone')
              }}</FloatingLabel>
              <input
                :id="`phone-${moduleId}`"
                v-model.trim="contactPhone"
                name="phone"
                type="tel"
                maxlength="16"
                @blur="startValidate(`phone`)"
              />
            </div>
          </div>

          <div
            v-for="(extraField, index) in extraFields"
            :key="index"
            class="form-field"
            :class="{ error: !isValidContactCustomField(index) }"
          >
            <FloatingLabel
              v-if="extraField.extraFieldLabel"
              :for="`extra-field-${index}`"
            >
              {{ extraField.extraFieldLabel }}*
            </FloatingLabel>
            <input
              :id="`extra-field-${index}`"
              v-model.trim="contactExtraFields[index]"
              :name="`extra-field-${index}`"
              @blur="startValidate(`custom-field-${index}`)"
            />
          </div>

          <div
            class="form__optin form__optin--options"
            :class="{ error: !isValidContactOption }"
          >
            <label>
              <input
                v-model="contactOption"
                type="radio"
                value="check"
                @blur="startValidate('option')"
              />
              <span v-html="$t('consultRegistrationData')"></span>
            </label>
            <label>
              <input
                v-model="contactOption"
                type="radio"
                value="edit"
                @blur="startValidate('option')"
              />
              <span v-html="$t('changeRegistrationData')"></span>
            </label>
            <label>
              <input
                v-model="contactOption"
                type="radio"
                value="remove"
                @blur="startValidate('option')"
              />
              <span v-html="$t('deleteRegistrationData')"></span>
            </label>
          </div>

          <div
            v-if="isRemoving || editing"
            class="form__optin"
            :class="{ error: !isValidContactOptin }"
          >
            <input
              :id="optinId"
              v-model="contactOptin"
              type="checkbox"
              @blur="startValidate('optin')"
            />
            <label
              ref="consentText"
              :for="optinId"
            >
              {{ $t('termsMessage') }}
              <small v-html="removeConfirmationText"></small>
            </label>
          </div>

          <div class="form__footer">
            <div
              v-if="wasSent || editing"
              class="form__message"
              :class="{
                'form__message--success': wasSentWithSuccess,
                'form__message--error': !wasSentWithSuccess,
              }"
            >
              <span v-if="wasSentWithSuccess">
                {{ successMessage }}
              </span>
              <span v-else>
                {{ $t('errorMessage') }}
              </span>
            </div>
            <!-- popup exclusivo do Editor -->
            <div class="editor-form-disclaimer">
              <span class="slb-tooltip arrow-right-down">
                Os envios de formulários são desativados no preview. Para testar
                seu funcionamento, acesse sua página publicada.
              </span>
              <!-- botão desabilitado no Editor -->
              <button
                type="submit"
                class="cta"
                :disabled="true"
              >
                {{ submitButtonLabel }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { IMaskDirective } from 'vue-imask';

import base from '@/mixins/base';
import isValidCpf from '@/validation/validators/cpf';
import uniqueId from '@/helpers/uniqueId';
import BaseModule from './generic/BaseModule.vue';
import FloatingLabel from '../components/site/FloatingLabel.vue';

export default {
  name: 'FormLgpd',
  components: {
    BaseModule,
    FloatingLabel,
  },
  directives: {
    imask: IMaskDirective,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    backgroundColor: {
      type: String,
      default: '#ffffff', // overrides default
    },
    text: {
      type: String,
      default: '',
    },
    successMessage: {
      type: String,
      default: '',
    },
    removeConfirmationText: {
      type: String,
      default: '',
    },
    extraFields: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      contactName: '',
      contactCpf: '',
      contactEmail: '',
      contactPhone: '',
      contactOption: null,
      contactOptin: false,
      optinId: null,
      contactExtraFields: [],
      isSending: false,
      validatingFields: [],
      wasSent: false,
      wasSentWithSuccess: true,
      contactCpfMask: {
        mask: '000.000.000-00',
      },
    };
  },
  computed: {
    maskedContactCpf: {
      get() {
        return this.contactCpf;
      },
      set() {
        // Do nothing here. Mask's accept event will do the job
      },
    },
    submitButtonLabel() {
      if (this.isSending) {
        return this.$t('sendingText');
      }

      return this.$t('submitButton');
    },
    isRemoving() {
      return this.contactOption === 'remove';
    },
    isCpfValid() {
      return isValidCpf(this.contactCpf);
    },
    editing() {
      return typeof this.$parent === 'undefined';
    },
    isValidContactName() {
      return !this.validatingFields.includes('name') || this.contactName !== '';
    },
    isValidContactCpf() {
      return !this.validatingFields.includes('cpf') || this.isCpfValid;
    },
    isValidContactEmail() {
      return (
        !this.validatingFields.includes('email') ||
        this.isValidEmail(this.contactEmail)
      );
    },
    isValidContactPhone() {
      // Phones must have at least 10 numbers
      return (
        !this.validatingFields.includes('phone') ||
        this.contactPhone.replace(/\D/g, '').length >= 10
      );
    },
    isValidContactOption() {
      return (
        !this.validatingFields.includes('option') || this.contactOption !== null
      );
    },
    isValidContactOptin() {
      return (
        !this.validatingFields.includes('optin') ||
        !this.isRemoving ||
        this.contactOptin
      );
    },
    moduleId() {
      return this.instanceId.replace(/\D/g, '');
    },
  },
  created() {
    this.contactExtraFields = this.extraFields.map(() => '');
  },
  mounted() {
    this.optinId = uniqueId();
  },
  methods: {
    updateContactCpf(event) {
      this.contactCpf = event.detail.value;
    },
    isValid() {
      this.startValidate('name');
      this.startValidate('cpf');
      this.startValidate('email');
      this.startValidate('phone');
      this.startValidate('option');
      this.startValidate('optin');

      let contactCustomFieldsAreValid = true;

      this.contactExtraFields.forEach((field, index) => {
        this.startValidate(`custom-field-${index}`);

        contactCustomFieldsAreValid =
          contactCustomFieldsAreValid && this.isValidContactCustomField(index);
      });

      return (
        this.isValidContactName &&
        this.isValidContactCpf &&
        this.isValidContactEmail &&
        this.isValidContactPhone &&
        this.isValidContactOption &&
        this.isValidContactOptin &&
        contactCustomFieldsAreValid
      );
    },
    isValidEmail(email) {
      const emailPattern =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      return emailPattern.test(email);
    },
    send() {
      this.wasSent = false;

      if (this.isValid()) {
        this.isSending = true;

        const body = {
          name: this.contactName,
          cpf: this.contactCpf,
          email: this.contactEmail,
          phone: this.contactPhone,
          option: this.contactOption,
          extraFields: [],
        };

        for (let idx = 0; idx < this.contactExtraFields.length; idx += 1) {
          body.extraFields.push({
            label: this.extraFields[idx].extraFieldLabel,
            value: this.contactExtraFields[idx],
          });
        }

        if (this.isRemoving) {
          body.extraFields.push({
            label: 'Termos aceitos',
            value: this.$refs.consentText.textContent
              .replace(/\s+/g, ' ')
              .trim(),
          });
        }

        const { apiBaseUrl, customer } = this.$store.state;

        fetch(`${apiBaseUrl}lgpd/${customer}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: JSON.stringify(body),
        })
          .then((res) => {
            this.wasSentWithSuccess = res.status === 200;

            if (this.wasSentWithSuccess) {
              this.resetForm();
            }
          })
          .catch(() => {
            this.wasSentWithSuccess = false;
          })
          .finally(() => {
            this.isSending = false;
            this.wasSent = true;
          });
      }
    },
    resetForm() {
      this.contactName = '';
      this.contactCpf = '';
      this.contactEmail = '';
      this.contactPhone = '';
      this.contactOption = null;
      this.contactOptin = false;
      this.contactExtraFields = this.contactExtraFields.map(() => '');
      this.validatingFields = [];
    },
    startValidate(field) {
      if (!this.validatingFields.includes(field)) {
        this.validatingFields.push(field);
      }
    },
    isValidContactCustomField(index) {
      return (
        !this.validatingFields.includes(`custom-field-${index}`) ||
        (typeof this.contactExtraFields[index] === 'string' &&
          this.contactExtraFields[index] !== '')
      );
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
              },
              {
                name: 'removeConfirmationText',
                type: 'Html',
                label: 'Nota de exclusão de dados',
                wrapWithParagraph: false,
              },
              {
                name: 'successMessage',
                type: 'LongText',
                label: 'Mensagem de confirmação',
              },
            ],
          },
          {
            name: 'extraFields',
            label: 'Campos extras',
            fields: [
              {
                name: 'extraFields',
                type: 'List',
                maxItems: 3,
                itemLabel({ extraFieldLabel }) {
                  return extraFieldLabel || 'Campo extra {index}';
                },
                addItemLabel: 'Adicionar campo extra',
                fields: [
                  {
                    name: 'extraFieldLabel',
                    type: 'ShortText',
                    label: 'Nome do campo',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        placeHolderName: 'Nome completo*',
        placeHolderPhone: 'Telefone*',
        consultRegistrationData:
          'Quero <strong>consultar</strong> meus dados cadastrais',
        changeRegistrationData:
          'Quero <strong>alterar</strong> meus dados cadastrais',
        deleteRegistrationData:
          'Quero <strong>excluir</strong> meus dados cadastrais',
        errorMessage: 'Ops! Algo errado aconteceu ao enviar seu contato.',
        submitButton: 'Enviar',
        sendingText: 'Enviando...',
        termsMessage:
          'Tenho certeza e concordo com os termos da exclusão de dados.',
      },
      en: {
        placeHolderName: 'Full name*',
        placeHolderPhone: 'Phone*',
        consultRegistrationData:
          'I want to <strong>consult</strong> my registration data',
        changeRegistrationData:
          'I want to <strong>change</strong> my registration data',
        deleteRegistrationData:
          'I want to <strong>delete</strong> my registration data',
        errorMessage:
          'Oops! Something wrong happened when sending your contact.',
        submitButton: 'Send',
        sendingText: 'Sending...',
        termsMessage:
          'I am sure and agree with the terms of the data deletion.',
      },
      es: {
        placeHolderName: 'Nombre completo*',
        placeHolderPhone: 'Teléfono*',
        consultRegistrationData:
          'Quiero <strong>consultar</strong> mis datos de registro',
        changeRegistrationData:
          'Quiero <strong>cambiar</strong> mis datos de registro',
        deleteRegistrationData:
          'Quiero <strong>borrar</strong> mis datos de registro',
        errorMessage: '¡UPS! Ocurrió algo incorrecto al enviar su contacto.',
        submitButton: 'Entregar',
        sendingText: 'Enviando...',
        termsMessage:
          'Estoy seguro y estoy de acuerdo con los términos de la eliminación de datos.',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.module-form {
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(md) {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }

  .txt {
    margin: 0 auto;
    max-width: 700px;
    text-align: left;
    white-space: pre-line;

    &::v-deep {
      p {
        font-size: 1em;
      }

      strong {
        font-weight: $lb-bold !important;
      }

      a,
      a:visited {
        color: inherit;
        text-decoration: underline;
      }
    }
  }

  .form {
    width: 100%;

    .tit-m {
      font-weight: $lb-light;
    }

    .field-2 {
      display: flex;

      .form-field {
        position: relative;
        width: calc(100% / 2);

        + .form-field {
          margin-left: 10px;
        }
      }
    }

    .form-field {
      position: relative;
      width: 100%;
      margin-top: 16px;

      input,
      select {
        border: 1px solid $lb-medium-gray;
        background-color: $lb-white;
        border-radius: 4px;
        width: 100%;
        font-style: normal;
        font-size: $size-small;
        line-height: 24px;
        outline: none;
        padding: 20px 14px 6px;

        &.custom-field-select {
          min-height: 52px;
        }

        &:focus {
          border-color: $lb-black;
        }
      }
    }

    .error label {
      color: $lb-red;
      font-weight: $lb-semibold;
    }

    .error input,
    .error select {
      border-color: $lb-red !important;
      color: $lb-red;
      font-weight: $lb-semibold;
    }

    &__optin {
      align-items: flex-start;
      display: flex;
      margin-top: 2em;
      text-align: left;
      color: var(--text-color, $gray-900);

      input {
        margin: 0.25em 0.5em 0 0;
        cursor: pointer;
      }

      &.error input[type='checkbox'] {
        outline: 1px solid crimson;
      }

      small {
        display: block;

        &::v-deep strong {
          font-weight: $lb-bold !important;
        }
      }

      &--options {
        justify-content: space-between;

        @include media-breakpoint-down(lg) {
          flex-direction: column;
        }
      }
    }

    &__footer {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin-top: 2em;
    }

    &__message {
      margin-right: 2em;
      text-align: left;
      white-space: pre-line;

      &--success {
        color: green;
      }

      &--error {
        color: crimson;
      }
    }
  }
}
</style>
